import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, Check, X, Loader2, ExternalLink,
  UserPlus, UserMinus, User, UserCheck, Key, GripVertical, KeyRound, Settings2,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminLayout } from './AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const FEATURES = ['webspace','domains','vserver','dedi','discordbots','musicbots','licenses','teamspeak','gameserver'];
const FEATURE_LABELS = {
  webspace:'Webspace', domains:'Domains', vserver:'VServer', dedi:'Dedicated',
  discordbots:'Discord Bots', musicbots:'Musicbots', licenses:'Lizenzen',
  teamspeak:'TeamSpeak', gameserver:'Gameserver',
};
const EMPTY = { name:'', url:'', webspace:0, domains:0, vserver:0, dedi:0, discordbots:0, musicbots:0, licenses:0, teamspeak:0, gameserver:0 };

// ── Form ──────────────────────────────────────────────────────────────────────
function HostingForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial || EMPTY);
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Name *</label>
          <input value={form.name} onChange={e => setField('name', e.target.value)}
            data-testid="hosting-form-name" required placeholder="Anbieter Name"
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Website URL</label>
          <input value={form.url} onChange={e => setField('url', e.target.value)}
            data-testid="hosting-form-url" placeholder="https://anbieter.de"
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-2">Dienste</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FEATURES.map(k => (
            <label key={k} data-testid={`feature-toggle-${k}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                form[k] ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-slate-800 border-slate-700 text-slate-400'
              }`}>
              <input type="checkbox" checked={!!form[k]} onChange={e => setField(k, e.target.checked ? 1 : 0)} className="sr-only" />
              {form[k] ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : <X className="w-3.5 h-3.5 flex-shrink-0" />}
              {FEATURE_LABELS[k]}
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(form)} disabled={!form.name || loading} data-testid="hosting-form-save"
          className="flex-1 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Speichern
        </button>
        <button onClick={onCancel} data-testid="hosting-form-cancel"
          className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

// ── Provider account dialog ───────────────────────────────────────────────────
function ProviderAccountDialog({ hosting, onClose, authHeaders, API }) {
  const [account, setAccount] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [inviteForm, setInviteForm] = useState({ name: hosting?.name || '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetPw, setResetPw] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadAccount = useCallback(async () => {
    if (!hosting) return;
    const [accRes, keysRes] = await Promise.allSettled([
      axios.get(`${API}/admin/hostings/${hosting.id}/account`, { headers: authHeaders() }),
      axios.get(`${API}/admin/hostings/${hosting.id}/api-keys`, { headers: authHeaders() }),
    ]);
    if (accRes.status === 'fulfilled') setAccount(accRes.value.data);
    if (keysRes.status === 'fulfilled') setApiKeys(keysRes.value.data);
    setLoadingAccount(false);
  }, [hosting, API, authHeaders]);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/admin/hostings/${hosting.id}/account`, inviteForm, { headers: authHeaders() });
      await loadAccount();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const removeAccount = async () => {
    setSaving(true); setError('');
    try {
      await axios.delete(`${API}/admin/hostings/${hosting.id}/account`, { headers: authHeaders() });
      setAccount({ has_account: false });
      setApiKeys([]);
      setShowResetForm(false);
      setResetMsg('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setResetting(true); setResetMsg(''); setError('');
    try {
      await axios.post(
        `${API}/admin/hostings/${hosting.id}/reset-password`,
        { new_password: resetPw },
        { headers: authHeaders() }
      );
      setResetMsg('Passwort erfolgreich zurückgesetzt.');
      setResetPw('');
      setShowResetForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally { setResetting(false); }
  };

  return (
    <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-md" data-testid="provider-account-dialog">
      <p className="sr-only" id="provider-account-desc">Provider-Account für diesen Anbieter verwalten</p>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <User className="w-4 h-4 text-cyan-400" />
          Provider-Zugang: {hosting?.name}
        </DialogTitle>
      </DialogHeader>

      {loadingAccount ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>
      ) : account?.has_account ? (
        <div className="space-y-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-sm space-y-1.5">
            <p className="text-green-400 font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5" /> Account aktiv
            </p>
            <p><span className="text-slate-400">Name:</span> <span className="text-white">{account.name}</span></p>
            <p><span className="text-slate-400">E-Mail:</span> <span className="text-white">{account.email}</span></p>
            <p><span className="text-slate-400">Login-URL:</span>{' '}
              <a href="#/provider/login" className="text-cyan-400 hover:underline">/provider/login</a>
            </p>
          </div>

          {apiKeys.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5 text-amber-400" /> API-Schlüssel ({apiKeys.length})
              </p>
              <div className="space-y-1.5">
                {apiKeys.map(k => (
                  <div key={k.id} className="flex items-center justify-between text-xs">
                    <span className="text-slate-300 font-medium">{k.name}</span>
                    <span className="font-mono text-slate-500">{k.key_preview}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}
          {resetMsg && <p className="text-sm text-green-400">{resetMsg}</p>}

          {/* Passwort zurücksetzen */}
          {showResetForm ? (
            <form onSubmit={resetPassword} className="space-y-3 bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid="reset-pw-form">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-amber-400" /> Neues Passwort setzen
              </p>
              <input
                type="password"
                value={resetPw}
                onChange={e => setResetPw(e.target.value)}
                required
                minLength={8}
                placeholder="Min. 8 Zeichen"
                data-testid="reset-pw-input"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={resetting || resetPw.length < 8} data-testid="reset-pw-submit"
                  className="flex-1 py-2 bg-amber-500 text-slate-950 font-semibold rounded-lg hover:bg-amber-400 transition-colors text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                  Speichern
                </button>
                <button type="button" onClick={() => { setShowResetForm(false); setResetPw(''); }}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm transition-colors">
                  Abbrechen
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowResetForm(true)} data-testid="show-reset-pw-btn"
              className="w-full py-2.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-semibold rounded-lg hover:bg-amber-500/20 transition-colors text-sm flex items-center justify-center gap-2">
              <KeyRound className="w-4 h-4" />
              Passwort zurücksetzen
            </button>
          )}

          <button onClick={removeAccount} disabled={saving} data-testid="remove-provider-account-btn"
            className="w-full py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 font-semibold rounded-lg hover:bg-red-500/20 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
            Zugang entfernen
          </button>
        </div>
      ) : (
        <form onSubmit={invite} className="space-y-4" data-testid="invite-provider-form">
          <p className="text-sm text-slate-400">Dieser Anbieter hat noch keinen Account. Erstelle einen Zugang:</p>
          {error && <p className="text-sm text-red-400" data-testid="invite-error">{error}</p>}
          {[
            { key: 'name',     label: 'Name',     type: 'text',     placeholder: hosting?.name },
            { key: 'email',    label: 'E-Mail',   type: 'email',    placeholder: 'kontakt@anbieter.de' },
            { key: 'password', label: 'Passwort', type: 'password', placeholder: 'Min. 8 Zeichen' },
          ].map(({ key, label, type, placeholder }) => (
            <div key={key} className="space-y-1">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>
              <input type={type} value={inviteForm[key]} placeholder={placeholder}
                onChange={e => setInviteForm(f => ({ ...f, [key]: e.target.value }))}
                required data-testid={`invite-${key}`}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors" />
            </div>
          ))}
          <button type="submit" disabled={saving} data-testid="invite-provider-submit"
            className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Account erstellen & einladen
          </button>
        </form>
      )}
    </DialogContent>
  );
}

// ── Sortable row ──────────────────────────────────────────────────────────────
function SortableRow({ h, idx, onEdit, onDelete, onAccount, onDetail }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: h.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-testid={`provider-row-${idx}`}
      className="border-b border-slate-800/60 hover:bg-white/[0.02] transition-colors"
    >
      {/* Drag handle — hidden on mobile */}
      <td className="px-2 py-3 w-8 hidden md:table-cell">
        <button
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${idx}`}
          className="cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 transition-colors p-1 rounded"
          title="Ziehen zum Sortieren"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-white text-sm">{h.name}</span>
          {h.provider_account ? (
            <span data-testid={`account-badge-${idx}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-400 border border-green-500/20">
              <UserCheck className="w-3 h-3" /> Account
            </span>
          ) : (
            <span data-testid={`no-account-badge-${idx}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-700/50 text-slate-500 border border-slate-700">
              <User className="w-3 h-3" /> Kein Account
            </span>
          )}
        </div>
        {h.url && (
          <a href={h.url} target="_blank" rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-cyan-400 flex items-center gap-1 mt-0.5">
            {h.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </td>

      {FEATURES.map(k => (
        <td key={k} className="px-2 py-3 text-center hidden lg:table-cell">
          {h[k] ? <Check className="w-4 h-4 text-green-400 mx-auto" /> : <X className="w-4 h-4 text-slate-700 mx-auto" />}
        </td>
      ))}

      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 justify-end">
          <button onClick={() => onDetail(h)}
            data-testid={`detail-btn-${idx}`}
            title="Detailansicht"
            className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors">
            <Settings2 className="w-4 h-4" />
          </button>
          <button onClick={() => onAccount(h)}
            data-testid={`account-btn-${idx}`}
            title={h.provider_account ? 'Provider-Account verwalten' : 'Provider einladen'}
            className={`p-1.5 rounded-lg transition-colors ${
              h.provider_account
                ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                : 'text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10'
            }`}>
            {h.provider_account ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          </button>
          <button onClick={() => onEdit(h)}
            data-testid={`edit-btn-${idx}`}
            className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(h)}
            data-testid={`delete-btn-${idx}`}
            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function AdminProviders() {
  const { authHeaders, API } = useAdminAuth();
  const navigate = useNavigate();
  const [hostings, setHostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [accountDialog, setAccountDialog] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/hostings`, { headers: authHeaders() });
      setHostings(r.data);
    } finally { setLoading(false); }
  }, [API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = hostings.findIndex(h => h.id === active.id);
    const newIndex = hostings.findIndex(h => h.id === over.id);
    const reordered = arrayMove(hostings, oldIndex, newIndex);
    setHostings(reordered); // optimistic update

    setReordering(true);
    try {
      await axios.put(
        `${API}/admin/hostings/reorder`,
        { ids: reordered.map(h => h.id) },
        { headers: authHeaders() },
      );
    } catch {
      await load(); // revert on error
    } finally {
      setReordering(false);
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (dialog.mode === 'add') {
        await axios.post(`${API}/admin/hostings`, form, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/admin/hostings/${dialog.data.id}`, form, { headers: authHeaders() });
      }
      await load();
      setDialog(null);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/hostings/${id}`, { headers: authHeaders() });
    setDeleteConfirm(null);
    await load();
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Anbieter</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {hostings.length} Einträge
              {reordering && (
                <span className="ml-2 text-cyan-400 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Reihenfolge wird gespeichert…
                </span>
              )}
            </p>
          </div>
          <button onClick={() => setDialog({ mode: 'add' })} data-testid="add-hosting-btn"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm">
            <Plus className="w-4 h-4" /> Anbieter hinzufügen
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl overflow-hidden">
            {hostings.length > 0 && (
              <div className="px-4 py-2 border-b border-slate-800/60 bg-slate-900/30 text-xs text-slate-600 hidden md:flex items-center gap-2">
                <GripVertical className="w-3.5 h-3.5" />
                Zeilen ziehen zum Sortieren der Reihenfolge
              </div>
            )}
            <div className="overflow-x-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={hostings.map(h => h.id)} strategy={verticalListSortingStrategy}>
                  <table className="w-full" data-testid="providers-table">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="w-8 px-2 py-3 hidden md:table-cell"></th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Name</th>
                        {FEATURES.map(k => (
                          <th key={k} className="px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-400 hidden lg:table-cell">
                            <span className="hidden xl:inline">{FEATURE_LABELS[k]}</span>
                            <span className="xl:hidden">{FEATURE_LABELS[k].slice(0,3)}</span>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hostings.length === 0 ? (
                        <tr><td colSpan={FEATURES.length + 3} className="py-12 text-center text-slate-500 text-sm">
                          Noch keine Anbieter. Füge den ersten hinzu.
                        </td></tr>
                      ) : hostings.map((h, i) => (
                        <SortableRow
                          key={h.id}
                          h={h}
                          idx={i}
                          onEdit={h => setDialog({ mode: 'edit', data: h })}
                          onDelete={h => setDeleteConfirm(h)}
                          onAccount={h => setAccountDialog(h)}
                          onDetail={h => navigate(`/admin/providers/${h.id}`)}
                        />
                      ))}
                    </tbody>
                  </table>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={open => !open && setDialog(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'add' ? 'Anbieter hinzufügen' : 'Anbieter bearbeiten'}</DialogTitle>
          </DialogHeader>
          {dialog && <HostingForm initial={dialog.data} onSave={save} onCancel={() => setDialog(null)} loading={saving} />}
        </DialogContent>
      </Dialog>

      {/* Provider account dialog */}
      <Dialog open={!!accountDialog} onOpenChange={open => !open && setAccountDialog(null)}>
        {accountDialog && (
          <ProviderAccountDialog
            hosting={accountDialog}
            onClose={() => setAccountDialog(null)}
            authHeaders={authHeaders}
            API={API}
          />
        )}
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={open => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Anbieter löschen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">
            Soll <span className="text-white font-semibold">{deleteConfirm?.name}</span> wirklich gelöscht werden?
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => remove(deleteConfirm?.id)} data-testid="confirm-delete-btn"
              className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors text-sm">
              Ja, löschen
            </button>
            <button onClick={() => setDeleteConfirm(null)}
              className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
              Abbrechen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
